import React, { useRef, useState } from "react";
import {
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Camera,
  Edit,
  MapPin,
  Shield,
  Calendar,
  Building2,
  Lock
} from "lucide-react";
import axios from "axios";
import { Toast } from "primereact/toast";
import { API_BASE_URL } from "../../../constants";

const StaffProfile = () => {
  const [staffData, setStaffData] = useState({
    name: "Jane Smith",
    gender: "Female",
    email: "janesmith@gmail.com",
    phone: "9876543211",
    department: "Administration",
    designation: "Office Manager",
    location: "Delhi, India",
    dob: "1990-05-15",
    profilePicture:
      "https://xsgames.co/randomusers/assets/avatars/female/45.jpg",
  });

  const [editData, setEditData] = useState({ ...staffData });
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  // Password change state
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const toast = useRef(null);
  const toggleModal = () => setModalOpen(!modalOpen);
  const togglePasswordModal = () => setPasswordModalOpen(!passwordModalOpen);

  const validate = () => {
    const newErrors = {};
    if (!editData.name.trim()) newErrors.name = "Name is required.";
    if (!/^[0-9]{10}$/.test(editData.phone))
      newErrors.phone = "Phone number must be 10 digits.";
    if (!editData.department.trim())
      newErrors.department = "Department is required.";
    if (!editData.designation.trim())
      newErrors.designation = "Designation is required.";
    if (!editData.location.trim()) newErrors.location = "Location is required.";
    if (!editData.dob.trim()) newErrors.dob = "Date of Birth is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordChange = () => {
    const newErrors = {};
    if (!passwordData.currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required.";
    }
    if (!passwordData.newPassword.trim()) {
      newErrors.newPassword = "New password is required.";
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters.";
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }
    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSavingProfile(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setStaffData(editData);
      setModalOpen(true);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Profile updated successfully!",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update profile",
        life: 3000,
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!validatePasswordChange()) return;

    setIsChangingPassword(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          confirm_password: passwordData.confirmPassword,
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      if (response.data && response.data.status === "success") {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Password changed successfully!",
          life: 3000,
        });
        setPasswordModalOpen(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        const errorMsg = response.data.message || "Failed to change password";
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: errorMsg,
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Password change error:", error);
      const errorMsg = error.response?.data?.message ||
        error.response?.data?.error ||
        "An error occurred while changing password";
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: errorMsg,
        life: 3000,
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditData((prev) => ({ ...prev, profilePicture: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Toast ref={toast} />

      <nav className="navbar navbar-expand-lg navbar-dark bg-primary position-fixed w-100 z-3 ">
        <div className="container-fluid g-5">
          <a className="navbar-brand fw-bold" href="/">
            Staff
          </a>
        </div>
      </nav>

      <div className="container py-5" style={{ marginTop: "90px" }}>
        <div
          className="d-flex flex-column flex-md-row gap-4"
          style={{ minHeight: "80vh" }}
        >
          <div className="d-flex gap-4 w-100" style={{ flex: 1 }}>
            <Card className="shadow rounded-4 p-4 flex-grow-1" style={{ flex: 1 }}>
              <CardBody className="text-center">
                <div className="d-flex flex-column align-items-left position-relative gap-2">
                  <div className="position-relative">
                    <img
                      src={staffData.profilePicture}
                      alt="Profile"
                      className="rounded-circle shadow mb-2"
                      style={{ width: "120px", height: "120px", objectFit: "cover" }}
                    />
                  </div>
                  <h4 className="fw-bold d-flex flex-column flex-md-row align-items-center gap-2 mb-1">
                    <User size={20} /> <span>{staffData.name}</span>
                  </h4>
                  <p className="text-muted d-flex align-items-center gap-2 mb-1">
                    <Mail size={16} /> <span>{staffData.email}</span>
                  </p>
                  <p className="d-flex align-items-center gap-2 mb-1">
                    <Calendar size={16} /> <span>DOB: {staffData.dob}</span>
                  </p>
                  <p className="d-flex align-items-center gap-2 mb-1">
                    <Shield size={16} /> <span>{staffData.gender}</span>
                  </p>
                  <p className="d-flex align-items-center gap-2 mb-1">
                    <Phone size={16} /> <span>{staffData.phone}</span>
                  </p>
                  <p className="d-flex align-items-center gap-2 mb-1">
                    <Briefcase size={16} /> <span>{staffData.department}</span>
                  </p>
                  <p className="d-flex align-items-center gap-2 mb-1">
                    <Building2 size={16} /> <span>{staffData.designation}</span>
                  </p>
                  <p className="d-flex align-items-center gap-2 mb-1">
                    <MapPin size={16} /> <span>{staffData.location}</span>
                  </p>
                  <Button
                    color="secondary"
                    className="mt-2"
                    onClick={togglePasswordModal}
                  >
                    Change Password
                  </Button>
                </div>
              </CardBody>
            </Card>

            <Card className="shadow rounded-4 p-4 flex-grow-2" style={{ flex: 2 }}>
              <CardBody>
                <h3 className="fw-bold d-flex align-items-center gap-2 mb-4">
                  <Edit size={24} /> Edit Profile
                </h3>
                <Form>
                  <FormGroup className="mb-3">
                    <Label for="profilePicture">Profile Picture</Label>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="position-relative">
                        <img
                          src={editData.profilePicture}
                          alt="Preview"
                          className="rounded-circle shadow"
                          style={{ width: "80px", height: "80px", objectFit: "cover" }}
                        />
                        <div
                          className="position-absolute bottom-0 end-0 bg-white rounded-circle p-1 shadow"
                          style={{ cursor: "pointer" }}
                          onClick={() => document.getElementById("uploadImage").click()}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              document.getElementById("uploadImage").click();
                            }
                          }}
                        >
                          <Camera size={18} />
                        </div>
                      </div>
                      <Input
                        type="file"
                        id="uploadImage"
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-4">
                    <Label for="email">Email</Label>
                    <Input
                      type="email"
                      name="email"
                      id="email"
                      value={editData.email}
                      onChange={handleChange}
                      placeholder="Email"
                      className="rounded-3 p-2"
                      disabled
                    />
                  </FormGroup>

                  {["name", "phone", "dob", "department", "designation", "location"].map(
                    (field) => (
                      <FormGroup key={field} className="mb-3">
                        <Label for={field}>
                          {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, " $1")}
                        </Label>
                        <Input
                          type={field.includes("date") ? "date" : "text"}
                          name={field}
                          id={field}
                          value={editData[field]}
                          onChange={handleChange}
                          placeholder={field}
                          className="rounded-3 p-2"
                        />
                        {errors[field] && (
                          <div className="text-danger small">{errors[field]}</div>
                        )}
                      </FormGroup>
                    )
                  )}

                  <FormGroup className="mb-3">
                    <Label>Gender</Label>
                    <div className="d-flex gap-3">
                      {[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                        { label: "Other", value: "Other" },
                      ].map((option) => (
                        <FormGroup check key={option.value}>
                          <Input
                            type="checkbox"
                            name="gender"
                            checked={editData.gender === option.value}
                            onChange={() =>
                              setEditData((prev) => ({ ...prev, gender: option.value }))
                            }
                          />
                          <Label check>{option.label}</Label>
                        </FormGroup>
                      ))}
                    </div>
                  </FormGroup>

                  <Button
                    color="primary"
                    block
                    className="rounded-3 py-2 fw-semibold"
                    onClick={handleSave}
                    disabled={isSavingProfile}
                  >
                    {isSavingProfile ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      <Modal isOpen={modalOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Success 🎉</ModalHeader>
        <ModalBody>Profile updated successfully!</ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleModal}>
            OK
          </Button>
        </ModalFooter>
      </Modal>

      {/* Password Change Modal */}
      <Modal isOpen={passwordModalOpen} toggle={togglePasswordModal} centered>
        <ModalHeader toggle={togglePasswordModal}>
          <Lock size={20} className="me-2" />
          Change Password
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup className="mb-3">
              <Label for="currentPassword">Current Password</Label>
              <Input
                type="password"
                name="currentPassword"
                id="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                placeholder="Enter current password"
                className="rounded-3 p-2"
              />
              {passwordErrors.currentPassword && (
                <div className="text-danger small">
                  {passwordErrors.currentPassword}
                </div>
              )}
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="newPassword">New Password</Label>
              <Input
                type="password"
                name="newPassword"
                id="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter new password"
                className="rounded-3 p-2"
              />
              {passwordErrors.newPassword && (
                <div className="text-danger small">
                  {passwordErrors.newPassword}
                </div>
              )}
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="confirmPassword">Confirm New Password</Label>
              <Input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Confirm new password"
                className="rounded-3 p-2"
              />
              {passwordErrors.confirmPassword && (
                <div className="text-danger small">
                  {passwordErrors.confirmPassword}
                </div>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={togglePasswordModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handlePasswordSave}
            disabled={isChangingPassword}
          >
            {isChangingPassword ? (
              <>
                <Spinner size="sm" className="me-2" />
                Changing...
              </>
            ) : (
              "Change Password"
            )}
          </Button>
        </ModalFooter>
      </Modal>

    </>
  );
};

export default StaffProfile;
